import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { toast } from "sonner";
import { VERTICAL_MAP } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Briefcase, CheckCircle, Money, MapPin, CalendarCheck, CalendarBlank, Star, ChatText, WhatsappLogo } from "@phosphor-icons/react";
import { StarRating } from "@/components/ReviewModal";

export default function ProviderDashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState({ available: [], assigned: [] });
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("jobs");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return nav("/login");
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const p = await api.get("/providers/me");
      setProfile(p.data);
      if (p.data) {
        const [j, r] = await Promise.all([
          api.get("/providers/me/jobs"),
          api.get("/reviews/mine/received"),
        ]);
        setJobs(j.data);
        setReviews(r.data);
      }
    } finally { setLoading(false); }
  };

  const accept = async (id) => {
    try {
      await api.post(`/providers/jobs/${id}/accept`);
      toast.success("Job accepted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const complete = async (id) => {
    try {
      await api.post(`/providers/jobs/${id}/complete`);
      toast.success("Job completed · payout credited");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const confirmMomo = async (id) => {
    if (!window.confirm("Confirm Mobile Money received for this booking?")) return;
    try {
      await api.post(`/providers/jobs/${id}/confirm-momo`);
      toast.success("Mobile Money confirmed · booking marked paid");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-16 text-zinc-400">Loading…</div>;

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto p-16 text-center">
        <h1 className="font-serif-display text-3xl text-white">No application yet.</h1>
        <button onClick={() => nav("/become-provider")} className="mt-6 px-6 py-3 rounded-full bg-[#D4AF37] text-black">Apply now</button>
      </div>
    );
  }

  const avgRating = profile.rating_avg || 0;
  const ratingCount = profile.rating_count || 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="provider-dashboard">
      <div className="eyebrow mb-3">Provider workspace</div>
      <h1 className="font-serif-display text-4xl lg:text-5xl text-white">Hello, {user?.full_name?.split(" ")[0]}.</h1>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-10">
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-6">
          <Money size={22} className="text-[#D4AF37]" weight="duotone" />
          <div className="font-serif-display text-4xl text-white mt-3">${(profile.total_earnings || 0).toFixed(2)}</div>
          <div className="eyebrow mt-2">Total earnings</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-6">
          <Briefcase size={22} className="text-blue-400" weight="duotone" />
          <div className="font-serif-display text-4xl text-white mt-3">{jobs.available.length}</div>
          <div className="eyebrow mt-2">Available</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-6">
          <CheckCircle size={22} className="text-emerald-400" weight="duotone" />
          <div className="font-serif-display text-4xl text-white mt-3">{jobs.assigned.filter(j => j.status !== "completed").length}</div>
          <div className="eyebrow mt-2">Active</div>
        </div>
        <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="provider-rating-card">
          <Star size={22} className="text-[#D4AF37]" weight="fill" />
          <div className="font-serif-display text-4xl text-white mt-3">{avgRating > 0 ? avgRating.toFixed(1) : "—"}</div>
          <div className="eyebrow mt-2">Avg rating · {ratingCount}</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-6">
          <div className="font-serif-display text-4xl text-white mt-3">{profile.commission_pct}%</div>
          <div className="eyebrow mt-2">Platform fee</div>
        </div>
      </div>

      <div className="mt-12 flex items-center gap-4 flex-wrap">
        <span className="eyebrow">Your verticals:</span>
        {profile.verticals.map(v => {
          const m = VERTICAL_MAP[v];
          return <span key={v} className="px-4 py-1.5 rounded-full text-xs" style={{ background: `${m?.color}30`, border: `1px solid ${m?.color}` }}>{m?.name}</span>;
        })}
        <span className="px-4 py-1.5 rounded-full text-xs bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">Status: {profile.status}</span>
      </div>

      {/* TABS */}
      <div className="mt-14 flex gap-2 border-b border-white/10" data-testid="provider-tabs">
        {[
          { k: "jobs", label: "Jobs", Icon: Briefcase },
          { k: "calendar", label: "Calendar", Icon: CalendarBlank },
          { k: "reviews", label: `Reviews · ${ratingCount}`, Icon: ChatText },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-${t.k}`}
            className={`px-5 py-3 text-sm flex items-center gap-2 border-b-2 transition ${
              tab === t.k
                ? "border-[#D4AF37] text-[#D4AF37]"
                : "border-transparent text-zinc-400 hover:text-white"
            }`}
          >
            <t.Icon size={16} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "jobs" && (
        <>
          <section className="mt-10">
            <div className="eyebrow mb-6">Available jobs</div>
            {jobs.available.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#121214] p-10 text-center text-zinc-500">No available jobs matching your verticals.</div>
            ) : (
              <div className="space-y-3" data-testid="available-jobs">
                {jobs.available.map(j => (
                  <JobRow key={j.id} j={j} action={{ label: "Accept", onClick: () => accept(j.id), test: `accept-${j.id}` }} />
                ))}
              </div>
            )}
          </section>

          <section className="mt-12">
            <div className="eyebrow mb-6">Your jobs</div>
            {jobs.assigned.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-[#121214] p-10 text-center text-zinc-500">No jobs yet.</div>
            ) : (
              <div className="space-y-3" data-testid="my-jobs">
                {jobs.assigned.map(j => (
                  <JobRow
                    key={j.id}
                    j={j}
                    action={j.status === "completed" ? null : { label: "Mark completed", onClick: () => complete(j.id), test: `complete-${j.id}` }}
                    secondaryAction={
                      j.payment_mode === "mobile_money" && j.payment_status !== "paid" && j.status !== "completed" && j.status !== "cancelled"
                        ? { label: "Confirm MoMo", onClick: () => confirmMomo(j.id), test: `provider-confirm-momo-${j.id}` }
                        : null
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {tab === "calendar" && <CalendarView jobs={jobs.assigned} />}

      {tab === "reviews" && <ReviewsList reviews={reviews} />}
    </div>
  );
}

// ============================================================
// CALENDAR — weekly grid
// ============================================================
const CalendarView = ({ jobs }) => {
  const [weekOffset, setWeekOffset] = useState(0);

  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay() + weekOffset * 7);
  startOfWeek.setHours(0, 0, 0, 0);

  const days = [...Array(7)].map((_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  const jobsByDay = days.map(d => {
    const key = d.toDateString();
    return jobs
      .filter(j => new Date(j.scheduled_at).toDateString() === key)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  });

  const total = jobsByDay.reduce((sum, jl) => sum + jl.length, 0);

  return (
    <div className="mt-10" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-1">Week of</div>
          <div className="font-serif-display text-2xl text-white">
            {startOfWeek.toLocaleDateString("en-US", { month: "long", day: "numeric" })} —
            {" "}
            {new Date(startOfWeek.getTime() + 6 * 86400000).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </div>
          <div className="text-xs text-zinc-500 mt-1">{total} scheduled job{total !== 1 ? "s" : ""}</div>
        </div>
        <div className="inline-flex gap-1 rounded-full border border-white/10 bg-black/40 p-1 text-sm">
          <button
            onClick={() => setWeekOffset(w => w - 1)}
            data-testid="cal-prev-week"
            className="px-3 py-1.5 rounded-full text-zinc-300 hover:bg-white/5"
          >← Prev</button>
          <button
            onClick={() => setWeekOffset(0)}
            data-testid="cal-this-week"
            className={`px-3 py-1.5 rounded-full ${weekOffset === 0 ? "bg-[#D4AF37] text-black" : "text-zinc-300 hover:bg-white/5"}`}
          >This week</button>
          <button
            onClick={() => setWeekOffset(w => w + 1)}
            data-testid="cal-next-week"
            className="px-3 py-1.5 rounded-full text-zinc-300 hover:bg-white/5"
          >Next →</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d, i) => {
          const isToday = d.toDateString() === now.toDateString();
          const dayJobs = jobsByDay[i];
          return (
            <div
              key={i}
              data-testid={`day-${i}`}
              className={`rounded-2xl border p-4 min-h-[220px] ${
                isToday ? "border-[#D4AF37]/60 bg-[#D4AF37]/5" : "border-white/10 bg-[#121214]"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {d.toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div className={`font-serif-display text-2xl ${isToday ? "text-[#D4AF37]" : "text-white"}`}>
                  {d.getDate()}
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {dayJobs.length === 0 ? (
                  <div className="text-xs text-zinc-600 pt-2">—</div>
                ) : (
                  dayJobs.map(j => {
                    const m = VERTICAL_MAP[j.vertical];
                    return (
                      <div
                        key={j.id}
                        className="rounded-lg border border-white/5 bg-black/30 p-2 hover:border-[#D4AF37]/40 transition cursor-default"
                        style={{ borderLeft: `3px solid ${m?.color}` }}
                        title={`${j.service_name} · ${j.address}`}
                      >
                        <div className="text-xs font-medium text-white truncate">{j.service_name}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          {new Date(j.scheduled_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · {j.region === "liberia" ? "🇱🇷" : "🇺🇸"}
                        </div>
                        <div className="text-[10px] text-[#D4AF37] mt-0.5">${j.provider_payout.toFixed(0)}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// REVIEWS LIST
// ============================================================
const ReviewsList = ({ reviews }) => (
  <div className="mt-10" data-testid="reviews-list">
    {reviews.length === 0 ? (
      <div className="rounded-2xl border border-white/10 bg-[#121214] p-16 text-center text-zinc-500">
        <Star size={40} weight="duotone" className="text-zinc-600 mx-auto mb-4" />
        No reviews yet. Complete more jobs to earn ratings.
      </div>
    ) : (
      <div className="space-y-3">
        {reviews.map(r => (
          <div key={r.id} className="rounded-2xl border border-white/10 bg-[#121214] p-6" data-testid={`review-${r.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <StarRating value={r.rating} readOnly size={16} testidPrefix={`rating-${r.id}`} />
                  <span className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <div className="mt-3 font-serif-display text-xl text-white">{r.service_name}</div>
                <div className="text-xs text-zinc-500">{r.customer_name} · {r.region === "liberia" ? "🇱🇷" : "🇺🇸"}</div>
              </div>
            </div>
            {r.comment && <div className="mt-4 text-sm text-zinc-300 italic leading-relaxed">"{r.comment}"</div>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const JobRow = ({ j, action, secondaryAction }) => {
  const m = VERTICAL_MAP[j.vertical];
  const flag = j.region === "liberia" ? "🇱🇷" : "🇺🇸";
  const payLabel =
    j.payment_mode === "reserve" ? "Cash on arrival"
    : j.payment_mode === "mobile_money" ? (j.payment_status === "paid" ? "Mobile Money · Paid" : "Mobile Money · Pending")
    : "Prepaid";

  // One-tap WhatsApp link for the provider — routes to recipient for gifts, else customer.
  const gift = j.is_gift;
  const waPhone = gift ? (j.gift_recipient_phone || j.customer_phone) : j.customer_phone;
  const waName = gift ? (j.gift_recipient_name || "recipient") : (j.customer_name || "customer");
  const waDigits = String(waPhone || "").replace(/[^\d]/g, "");
  const waFirst = String(waName).split(" ")[0] || "there";
  const waHref = waDigits ? `https://wa.me/${waDigits}?text=${encodeURIComponent(
    [
      `Hi ${waFirst}, this is your RideXelite provider about reservation ${j.reservation_ref || j.id}.`,
      "",
      `Service: ${j.service_name}`,
      j.scheduled_at ? `Scheduled: ${new Date(j.scheduled_at).toLocaleString()}` : "",
      j.address ? `Pickup / address: ${j.address}` : "",
      gift && !j.gift_recipient_phone ? `(Gift booked by ${j.customer_name || 'the sender'})` : "",
    ].filter(Boolean).join("\n")
  )}` : null;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#121214] p-6 flex flex-wrap items-center gap-6 hover:border-[#D4AF37]/40 transition">
      <div className="w-1.5 h-12 rounded-full" style={{ background: m?.color }} />
      <div className="flex-1 min-w-[200px]">
        <div className="font-serif-display text-xl text-white">{j.service_name}</div>
        <div className="text-xs text-zinc-500 uppercase tracking-widest">RideXelite {m?.name}</div>
        {j.reservation_ref && (
          <div className="mt-1 text-[10px] text-[#D4AF37] tracking-widest">REF: {j.reservation_ref}</div>
        )}
      </div>
      <div className="text-sm text-zinc-400 flex items-center gap-2"><span>{flag}</span>{j.region_label || (j.region === "liberia" ? "Liberia" : "USA")}</div>
      <div className={`text-xs px-2 py-1 rounded-full border ${j.payment_mode === "mobile_money" && j.payment_status !== "paid" ? "bg-[#D4AF37]/10 border-[#D4AF37]/40 text-[#D4AF37]" : "bg-white/5 border-white/10 text-zinc-400"}`}>{payLabel}</div>
      <div className="text-sm text-zinc-400 flex items-center gap-2"><CalendarCheck size={16} />{new Date(j.scheduled_at).toLocaleString()}</div>
      <div className="text-sm text-zinc-400 flex items-center gap-2 max-w-xs truncate"><MapPin size={16} />{j.address}</div>
      <div className="text-right">
        <div className="font-serif-display text-lg text-white">${j.provider_payout.toFixed(2)}</div>
        <div className="text-xs text-zinc-500">payout · ${j.amount.toFixed(2)} total</div>
      </div>
      <div className="flex flex-col gap-2 items-end">
        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`provider-wa-${j.id}`}
            data-sound="coin"
            title={`WhatsApp ${waName}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#25D366]/40 text-[#25D366] text-[10px] uppercase tracking-[0.15em] hover:bg-[#25D366]/10 transition"
          >
            <WhatsappLogo size={12} weight="fill" /> WhatsApp
          </a>
        )}
        {secondaryAction && (
          <button
            onClick={secondaryAction.onClick}
            data-testid={secondaryAction.test}
            data-sound={secondaryAction.sound || "coin"}
            className="px-4 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.15em] hover:bg-[#D4AF37] hover:text-black transition"
          >
            {secondaryAction.label}
          </button>
        )}
        {action && (
          <button
            onClick={action.onClick}
            data-testid={action.test}
            data-sound={action.sound || "tock"}
            className="px-5 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition"
          >
            {action.label}
          </button>
        )}
        {j.status === "completed" && <span className="px-3 py-1 rounded-full text-xs bg-[#D4AF37]/10 text-[#D4AF37]">Completed</span>}
      </div>
    </div>
  );
};
